import { useEffect, useReducer, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// API
import {
  authApiService,
  getStoredTokens,
  isTokenExpired,
  clearAuthTokens,
  setSession,
} from '../../../api/auth-api';
import { homeAPI } from '../../../api/home-api';

// Types
import {
  ActionMapType,
  AuthStateType,
  AuthUserType,
} from '../../types';
import {
  HTTaiKhoan,
  FuncsTagModel,
  KtToken,
} from '../../../types/api-response';

// Context
import { AuthContext } from './auth-context';

// Utils
import { isApiSuccess } from '../../../utils/api-helpers';
import { STORAGE_KEYS } from '../../../constants';

// ----------------------------------------------------------------------

enum Types {
  INITIAL = 'INITIAL',
  LOGIN = 'LOGIN',
  REGISTER = 'REGISTER',
  LOGOUT = 'LOGOUT',
}

type Payload = {
  [Types.INITIAL]: { user: AuthUserType | null };
  [Types.LOGIN]: { user: AuthUserType };
  [Types.REGISTER]: { user: AuthUserType };
  [Types.LOGOUT]: undefined;
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

const initialState: AuthStateType = {
  user: null,
  loading: true,
};

const reducer = (state: AuthStateType, action: ActionsType): AuthStateType => {
  switch (action.type) {
    case Types.INITIAL:
      return { loading: false, user: action.payload.user };
    case Types.LOGIN:
    case Types.REGISTER:
      return { ...state, user: action.payload.user };
    case Types.LOGOUT:
      return { ...state, user: null };
    default:
      return state;
  }
};

// ----------------------------------------------------------------------

function convertToAuthUser(htTaiKhoan?: HTTaiKhoan, funcsTagActives?: FuncsTagModel[],token?: String): AuthUserType {
  return {
    id: htTaiKhoan?.id || '',
    tenTaiKhoan: htTaiKhoan?.tenTaiKhoan || '',
    ten: htTaiKhoan?.tenTaiKhoan || '',
    email: htTaiKhoan?.email || '',
    sdt: htTaiKhoan?.sdt || '',
    ngayKhoiTao: htTaiKhoan?.ngayKhoiTao || '',
    trangThai: htTaiKhoan?.trangThai || 0,
    maVaiTros: htTaiKhoan?.htPhanQuyenTaiKhoans || [],
    isActive: htTaiKhoan?.trangThai === 1,
    permissions: funcsTagActives || [],
    matKhau: htTaiKhoan?.matKhau || '',
    authenticateToken: token || '',
    wallet: htTaiKhoan?.wallet,
    googleId : htTaiKhoan?.googleId
  };
}

function storeLoginData(tokenData: KtToken) {
  if (!tokenData) return;

  const {
    authenticateToken,
    refreshToken,
    expiredAuthenticateToken,
    expiredRefreshToken,
    service,
    htMenuModels,
    htTaiKhoan,
    funcsTagActives,
  } = tokenData;

  if (authenticateToken) {
    localStorage.setItem(STORAGE_KEYS.AUTHENTICATE_TOKEN, authenticateToken);
  }
  if (refreshToken) {
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
  }
  if (expiredAuthenticateToken) {
    localStorage.setItem(STORAGE_KEYS.EXPIRED_AUTHENTICATE_TOKEN, expiredAuthenticateToken);
  }
  if (expiredRefreshToken) {
    localStorage.setItem(STORAGE_KEYS.EXPIRED_REFRESH_TOKEN, expiredRefreshToken);
  }
  if (service) {
    localStorage.setItem(STORAGE_KEYS.SERVICE_URL, service);
  }
  if (htMenuModels) {
    localStorage.setItem(STORAGE_KEYS.MENU_ITEMS, JSON.stringify(htMenuModels));
  }
  if (htTaiKhoan) {
    localStorage.setItem(STORAGE_KEYS.USER_INFO, JSON.stringify(htTaiKhoan));
  }
  if (funcsTagActives) {
    localStorage.setItem(STORAGE_KEYS.PERMISSIONS, JSON.stringify(funcsTagActives));
  }
}

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};
export function AuthProvider({ children }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [, SetAuthToken] = useState<string | null>(null);
  const navigate = useNavigate();

const initialize = useCallback(async () => {
    try {
      const tokens = getStoredTokens();
      const currentToken = tokens?.authenticateToken || null;
      let user: AuthUserType | null = null;
      let shouldLogin = false;
      
      if (tokens && tokens.authenticateToken) {
        
        setSession(currentToken);

        const userInfoStr = localStorage.getItem(STORAGE_KEYS.USER_INFO);
        const permissionsStr = localStorage.getItem(STORAGE_KEYS.PERMISSIONS);

        if (userInfoStr && permissionsStr && !isTokenExpired(tokens.expiredAuthenticateToken)) {
          try {
            const htTaiKhoan = JSON.parse(userInfoStr);
            const funcsTagActives = JSON.parse(permissionsStr);
            user = convertToAuthUser(htTaiKhoan, funcsTagActives,tokens.authenticateToken);
            
           
            try {
               await authApiService.getCurrentUser();
            } catch (apiError: any) {
          
                if (apiError.response?.status === 401) {
                    // Cần kiểm tra lại tokens, nếu đã bị clear thì user = null
                    const newTokens = getStoredTokens();
                    if (!newTokens) throw new Error('Refresh Token thất bại');
                    
                } else {
                    // Nếu là lỗi khác (mất mạng, timeout), vẫn giữ user info từ localStorage
                    // và cho phép ứng dụng khởi động.
                }
            }
            
          } catch (error) {
            console.warn('Không thể parse user info từ localStorage:', error);
            shouldLogin = true; // Buộc phải login lại hoặc gọi getCurrentUser
          }
        } else if (!isTokenExpired(tokens.expiredAuthenticateToken)) {
             // 3. FALLBACK: Tải từ API nếu không có trong localStorage (Hoặc token dài hạn vẫn còn)
             // eslint-disable-next-line no-useless-catch
             try {
                const response = await authApiService.getCurrentUser();

                if (isApiSuccess(response) && response.oneItem) {
                  const { htTaiKhoan, funcsTagActives } = response.oneItem;
                  if(htTaiKhoan)
                  user = convertToAuthUser(htTaiKhoan, funcsTagActives || [],tokens.authenticateToken);
                } else {
                  throw new Error('Không thể lấy thông tin người dùng từ API');
                }
              } catch (error) {

                throw error;
              }
        } else {
          shouldLogin = true; // Token dài hạn hết hạn
        }
      } else {
         shouldLogin = true; // Không có token
      }

      // 4. KIỂM TRA LẠI SAU KHI TẤT CẢ LOGIC CHẠY XONG
      if (shouldLogin || !getStoredTokens()) {
        clearAuthTokens();
        setSession(null);
        dispatch({ type: Types.INITIAL, payload: { user: null } });
      } else if (user) {
        dispatch({ type: Types.INITIAL, payload: { user } });
      } else {
         // Trường hợp token hợp lệ nhưng user info bị lỗi hoặc bị clear do refresh thất bại
         const fallbackUser: AuthUserType = {
              id: 'temp-user',
              tenTaiKhoan: 'user',
              ten: 'User',
              email: '',
              sdt: '',
              ngayKhoiTao: new Date().toISOString(),
              trangThai: 1,
              maVaiTros: [],
              isActive: true,
              permissions: [],
              matKhau: '',
            };
        dispatch({ type: Types.INITIAL, payload: { user: fallbackUser } });
      }
      
    } catch (error) {
      // Xử lý lỗi cuối cùng (Refresh Token thất bại, hoặc lỗi mạng nghiêm trọng)
      clearAuthTokens();
      setSession(null);
      dispatch({ type: Types.INITIAL, payload: { user: null } });
    }
  }, []); 

  useEffect(() => {
    initialize();
  }, [initialize]);

  const login = useCallback(async (username: string, password: string) => {
    try {
      const response = await authApiService.login(username, password);

      if (isApiSuccess(response) && response.oneItem) {
        const tokenData = response.oneItem;
        storeLoginData(tokenData);
        setSession(tokenData.authenticateToken);
        const user = convertToAuthUser(tokenData.htTaiKhoan || undefined, tokenData.funcsTagActives || [],response.oneItem.authenticateToken ?? '');
        dispatch({ type: Types.LOGIN, payload: { user } });
        SetAuthToken(tokenData.authenticateToken)
        if(user?.maVaiTros.find((x: { maVaiTro: string }) => x.maVaiTro === 'nft_admin' || x.maVaiTro === 'nft_garden')){
           navigate('/dashboard')
        }else{
         navigate('/caysam/me') 
        }
      } else {
        throw new Error(response.message || 'Đăng nhập thất bại');
      }
    } catch (error: any) {
      console.error('Lỗi đăng nhập:', error);
      throw new Error(error.message || 'Đăng nhập thất bại');
    }
  }, [navigate]);

  const loginWithGoogle = useCallback(async (idToken?: string) => {
    try {
      if (!idToken) {
        throw new Error('Không tìm thấy token từ Google');
      }

      const response = await homeAPI.GoogleLogin({
        idToken,
        deviceToken: "string", 
      });

      if (response && response.oneItem) {
        const tokenData = response.oneItem;
        storeLoginData(tokenData);
        setSession(tokenData.authenticateToken);
        const user = convertToAuthUser(tokenData.htTaiKhoan || undefined, tokenData.funcsTagActives || [], tokenData.authenticateToken ?? '');
        dispatch({ type: Types.LOGIN, payload: { user } });
        SetAuthToken(tokenData.authenticateToken);
        if(user?.maVaiTros.find((x: { maVaiTro: string }) => x.maVaiTro === 'nft_admin' || x.maVaiTro === 'nft_garden')){
           navigate('/dashboard');
        }else{
         navigate('/caysam/me'); 
        }
      } else {
        throw new Error('Đăng nhập Google thất bại');
      }
    } catch (error: any) {
      console.error('Lỗi đăng nhập Google:', error);
      throw new Error(error.message || 'Đăng nhập Google thất bại');
    }
  }, [navigate]);

  const register = useCallback(async (email: string, password: string, username: string, sdt: string) => {
    try {
      const registerData = {
        tenTaiKhoan: username,
        matKhau: password,
        ngayKhoiTao: new Date().toISOString(),
        trangThai: 0,
        sdt,
        email,
      };

      const response = await authApiService.register(registerData);

      if (!isApiSuccess(response)) {
        throw new Error(response.message || 'Đăng ký thất bại');
      }

      console.log('Đăng ký thành công! Vui lòng đăng nhập.');
    } catch (error: any) {
      console.error('Lỗi đăng ký:', error);
      throw new Error(error.message || 'Đăng ký thất bại');
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApiService.logout();
    } catch (error) {
      console.error('Lỗi khi logout:', error);
    } finally {
      SetAuthToken(null);
      clearAuthTokens();
      setSession(null);
      dispatch({ type: Types.LOGOUT });
      navigate('/'); // ✅ Điều hướng sau đăng xuất
    }
  }, [navigate]);

  const status = useMemo(() => {
    if (state.loading) return 'loading';
    if (state.user) return 'authenticated';
    return 'unauthenticated';
  }, [state.loading, state.user]);

  const memoizedValue = useMemo(() => ({
    user: state.user,
    method: 'jwt',
    loading: status === 'loading',
    authenticated: status === 'authenticated',
    unauthenticated: status === 'unauthenticated',
    login,
    loginWithGoogle,
    register,
    logout,
  }), [login, loginWithGoogle, logout, register, state.user, status]);

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
